import {
  computed,
  onMounted,
  watch,
} from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'vue-router';

import ToastNotification from '@/contexts/notificationsContext/components/ToastNotification/index.vue';
import { ENotificationPermission, ENotificationType } from '@/contexts/notificationsContext/domain/constants';
import useWebsocketChannel from '@/composables/useWebsocketChannel';
import {
  notification,
  isString,
  logger,
} from '@/utils';
import { toggleAppTitleWithNotifications } from '@/contexts/notificationsContext/domain/toggleAppTitleWithNotifications';
import getNotificationScheme from '@/contexts/notificationsContext/domain/getNotificationScheme';
import { useTenantsStore } from '@/stores/tenants';
import { useAppStore } from '@/stores/app';
import { checkFeatureIsEnabled } from '@/domains/checkFeatureIsEnabled';
import { EExperimentalFeatures } from '@/domains/constants';
import { TNotificationSchemeItem } from '@/contexts/notificationsContext/domain/types';
import getActionPath from '@/contexts/notificationsContext/components/Notification/domain/getActionPath';
import { TServiceWorkerMessage, TServiceWorkerNotificationPayload } from '@/service-worker';
import setDefaultFavicon from '@/domains/setDefaultFavicon';
import setNotificationFavicon from '@/domains/setNotificationFavicon';
import { useNotificationStore } from '@/stores/notifications';

// TODO: переместить тип выше и описать тип приходящий сообщений для всех каналов
type TWebsocketMessagePayload = {
  payload: Record<string, string> | null,
  type: ENotificationType,
  id: string,
};

const useNotificationsUtils = () => {
  const tenantsStore = useTenantsStore();
  const appStore = useAppStore();
  const notificationStore = useNotificationStore();
  const router = useRouter();

  let serviceWorkerRegistration: Promise<ServiceWorkerRegistration> | null = null;

  const isShowPushNotificationAvailable = computed(
    () => checkFeatureIsEnabled(EExperimentalFeatures.showPushNotification),
  );

  const { start: startToggle, stop: stopToggle } = toggleAppTitleWithNotifications();

  const showDefaultNotification = (message: string, notificationId: string) => {
    const toastKey = uuidv4();
    notification.info({
      message: (
        <ToastNotification
          message={message}
          notificationId={notificationId}
          toastKey={toastKey}
        />
      ),
      key: toastKey,
    });
  };

  const showPushNotification = async (payload: TServiceWorkerNotificationPayload) => {
    try {
      if (!serviceWorkerRegistration) throw Error();

      const registration: ServiceWorkerRegistration = await serviceWorkerRegistration;

      // Ожидаем готовности Service Worker
      await navigator.serviceWorker.ready;

      const serviceWorkerMessage: TServiceWorkerMessage<TServiceWorkerNotificationPayload> = {
        action: 'showNotification',
        payload,
      };

      // шлем сообщение в активный Service Worker, обработчик которого вызовет отображение нотификации
      registration.active?.postMessage(serviceWorkerMessage);
    } catch {
      /**
       * Вызываем уведомление через new Notification для браузеров, которые не поддерживают отображение через showNotification,
       * либо в случае проблем с Service Worker.
       */
      const { options, title } = payload;
      const notification = new Notification(title, options);

      // добавляем обработчик клика по уведомлению. Сработает пока активна вкладка, вызвавшая отображение нотификации
      notification.onclick = () => {
        notification.close();

        // фокусируемся на окне, которое стало источником уведомления
        window.focus();

        if (options.data.relativePath) {
          router.push(options.data.relativePath);
        }
      };
    }
  };

  const getPushNotificationPayload = (
    { id, payload }: TWebsocketMessagePayload,
    schemeItem: TNotificationSchemeItem,
  ): TServiceWorkerNotificationPayload => {
    const notificationMessage = isString(schemeItem.template)
      ? schemeItem.template
      : schemeItem.template(payload);

    const schemeItemAction = schemeItem.actions.length ? schemeItem.actions[0] : null;
    const relativePath = schemeItemAction && payload ? getActionPath(schemeItemAction.path, { payload }) : null;

    return {
      title: schemeItem.title,
      options: {
        data: {
          redirectUrl: relativePath ? `${import.meta.env.VITE_APP_HOST}${relativePath}` : null,
          relativePath,
        },
        body: notificationMessage,
        tag: id,
        icon: '/default/icon-192.png',
        renotify: true,
      },
    };
  };

  const showNotification = (message: TWebsocketMessagePayload, schemeItem: TNotificationSchemeItem) => {
    /**
     * Если выключен ФФ или не доступен объект window.Notification или пользователь еще не дал разрешение на показ уведомлений,
     * то показываем веб уведомление (из библиотеки Ant)
     *
     * После выпила ФФ showPushNotification, оставить только !window.Notification || window.Notification.permission !== ENotificationPermission.granted
    */
    if (
      !isShowPushNotificationAvailable.value
      || !window.Notification
      || window.Notification.permission !== ENotificationPermission.granted
    ) {
      const messageTemplate = schemeItem.template;
      const notificationMessage = isString(messageTemplate)
        ? messageTemplate
        : messageTemplate(message.payload);

      showDefaultNotification(notificationMessage, message.id);

      return;
    }

    const notificationPayload = getPushNotificationPayload(message, schemeItem);

    showPushNotification(notificationPayload);
  };

  const handleNewNotification = (message: TWebsocketMessagePayload) => {
    if (!message) return;

    if (message.type === ENotificationType.runExecutionContextExecutorsNotifyAboutPlannedRun) return;

    notificationStore.setUnreadNotificationsCount(notificationStore.unreadCount + 1);
    const schemeItem = getNotificationScheme(message.payload)[message.type];

    if (!schemeItem) return;

    showNotification(message, schemeItem);

    if (!appStore.isBrowserTabFocused) {
      notificationStore.setBrowserTabWithUnreadNotifications(true);
    }
  };

  const { connect, closeConnection } = useWebsocketChannel({
    channelName: 'NotificationsChannel',
    onMessage: handleNewNotification,
  });

  const connectToNotificationChannel = () => {
    connect();
  };

  const disconnectFromNotificationChannel = () => {
    closeConnection();
  };

  const reconnectNotificationChannel = () => {
    disconnectFromNotificationChannel();
    connectToNotificationChannel();
  };

  const registerServiceWorker = () => {
    // если браузер не поддерживает Service worker или Service Worker не поддерживает отображение уведомлений (версии Safari до 16)
    if (!navigator.serviceWorker || (typeof ServiceWorkerRegistration.prototype.showNotification !== 'function')) {
      serviceWorkerRegistration = null;

      logger.log('[registerNotificationServiceWorker] Browser does not support service worker or service worker is not available showNotification');

      return;
    }

    const serviceWorkerPath = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/service-worker.js';

    serviceWorkerRegistration = navigator.serviceWorker.register(serviceWorkerPath);

    serviceWorkerRegistration
      .catch((error: unknown) => {
        logger.log('[registerNotificationServiceWorker] Service worker registration error: ', error);
      });
  };

  const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    if (!window.Notification) return ENotificationPermission.default;

    try {
      return await window.Notification.requestPermission();
    } catch (error: unknown) {
      logger.log('[requestNotificationPermission] Request permission error: ', error);

      return window.Notification.permission;
    }
  };

  watch(() => tenantsStore.currentTenant?.id, () => {
    if (!tenantsStore.currentTenant?.id) return;
    reconnectNotificationChannel();
  });

  watch(
    () => notificationStore.isBrowserTabWithUnreadNotifications,
    (isBrowserTabWithUnreadNotifications: boolean) => {
      if (isBrowserTabWithUnreadNotifications) {
        startToggle();
        setNotificationFavicon();
      } else {
        stopToggle();
        setDefaultFavicon();
      }
    });

  watch(() => appStore.isBrowserTabFocused, (isTabActive: boolean) => {
    if (isTabActive) {
      notificationStore.setBrowserTabWithUnreadNotifications(false);
    }
  });

  onMounted(() => {
    watch(isShowPushNotificationAvailable, () => {
      if (isShowPushNotificationAvailable.value && window.Notification) {
        // если решение пользователя по отображению уведомлений не известно
        if (window.Notification.permission === ENotificationPermission.default) {
          // запрашиваем разрешение на показ уведомлений
          requestNotificationPermission()
            .then((permission: NotificationPermission) => {
              if (permission === ENotificationPermission.granted) {
                registerServiceWorker();
              }
            })
            .catch(() => {});
        // если пользователь дал разрешение на показ уведомлений
        } else if (window.Notification.permission === ENotificationPermission.granted) {
          registerServiceWorker();
        }
      }
    }, { immediate: true });
  });
};

export default useNotificationsUtils;
